import { ROUTES } from '@configs'
import { useChatBotStore } from '@store'
import { NovaModalDialog } from '#components'

export const useChatbot = () => {
  return {
    checkAvailableChatbot,
    goChatbot,
    leaveConfirm,
  }
}

/**
 * 챗봇의 사용 가능 여부를 확인하는 데 필요한 파라미터
 */
interface CheckAvailableChatbotParams {
  uuid: string
  onSuccessCheck?: (uuid: string) => void
  onFailCheck?: (err: unknown, uuid: string) => void
}

/**
 * 챗봇의 사용 가능 여부를 비동기로 확인하며, 챗봇 스토어와 연동하여 필요한 메서드를 호출한다.
 *
 * @async
 * @function checkAvailableChatbot
 * @param {Object} params - 챗봇 사용 가능 여부 확인을 위한 파라미터
 * @param {string} params.uuid - 챗봇을 식별하는 고유 ID
 * @param {function} [params.onSuccessCheck] - 챗봇 확인이 성공했을 때 호출되는 콜백 함수로, UUID를 인자로 받는다.
 * @param {function} [params.onFailCheck] - 챗봇 확인이 실패했을 때 호출되는 콜백 함수로, 에러 객체와 UUID를 인자로 받는다.
 * @throws {Error} 챗봇 사용 가능 여부를 확인하는 API 호출이 실패한 경우 에러를 발생시킨다.
 */
const checkAvailableChatbot = async ({
  uuid,
  onSuccessCheck,
  onFailCheck,
}: CheckAvailableChatbotParams) => {
  const chatBotStore = useChatBotStore()

  try {
    // 2. 사용량 체크 API 호출
    await chatBotStore.chatBotOpening({ message: '', uuid })
    onSuccessCheck?.(uuid)
  } catch (err) {
    onFailCheck?.(err, uuid)
  }
}

interface goChatbotParams {
  goTo: 'modal' | 'page'
  onSuccessCheck?: (uuid: string) => void
  onFailCheck?: (err: unknown, uuid: string) => void
}

const goChatbot = async ({
  goTo,
  onSuccessCheck,
  onFailCheck,
}: goChatbotParams) => {
  const uuid = useGenUUID()

  const goModal = async () => {
    await checkAvailableChatbot({
      uuid,
      onSuccessCheck,
      onFailCheck,
    })
  }

  const goPage = async () => {
    await useNavigations({
      url: useRoutePathIdChange(ROUTES.CHATBOT.path, {
        uuid,
      }),
    })
  }

  switch (goTo) {
    case 'modal':
      await goModal()
      break
    case 'page':
      await goPage()
      break
    default:
      throw createError({
        statusCode: 500,
        statusMessage: 'not allow go chatbot to type',
      })
  }
}

const leaveConfirm = (
  t: ReturnType<typeof useI18n>['t'],
  onConfirm: () => void
) => {
  const { show: modalShow, hide: modalHide } = useModal()
  const { gtEvent } = useGoogleTag()
  console.log(t)
  console.log(onConfirm)

  modalShow({
    component: NovaModalDialog,
    bind: {
      name: modalsName.MODAL_DIALOG,
      contentsTextAlign: 'center',
      btns: [
        {
          label: t('cancel'),
          theme: 'secondary-gray-200',
          size: 32,
          onClick: async () => {
            gtEvent('clickEvent', {
              eventCategory: '클릭',
              eventAction: '챗봇 모달 닫기 취소',
              eventLabel: '취소',
              eventSlot: '챗봇 모달 닫기 다이얼로그',
              eventI18nAddr: useKoreanTranslation('cancel'),
              eventComponent: 'NovaModalDialog',
            })
            await modalHide(modalsName.MODAL_DIALOG)
          },
        },
        {
          label: t('confirm'),
          theme: 'primary-blue-dark',
          size: 32,
          onClick: async () => {
            gtEvent('clickEvent', {
              eventCategory: '클릭',
              eventAction: '챗봇 모달 닫기 컨펌',
              eventLabel: '닫기',
              eventSlot: '챗봇 모달 닫기 다이얼로그',
              eventI18nAddr: useKoreanTranslation('confirm'),
              eventComponent: 'NovaModalDialog',
            })
            await modalHide(modalsName.MODAL_DIALOG)
            onConfirm?.()
            // useNavigations({ type: 'back' })
          },
        },
      ],
    },
    slots: {
      title: t('chatBot.dialog.title'),
      content: t('chatBot.dialog.message'),
    },
  })
}
